import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getProviderMigration,
  listProviderMigrationItems,
  type ProviderMigrationDetailDto,
  type ProviderMigrationItemDto,
} from './api/provider-migrations.api';

/**
 * LIVE MIGRATION STATE — bounded, server-authoritative polling.
 *
 * ── WHY THE PAGE WAS BLIND, AND WHAT ACTUALLY FIXES IT ─────────────────────
 * Two independent causes, both on the client:
 *
 *   1. `POST /run` is INLINE on the server: it copies and verifies every object
 *      before it responds. `platformApiClient` has `timeout: 30_000`, so a run
 *      of any real size is ABORTED BY THE BROWSER at 30 seconds while the server
 *      carries on to completion. The page then showed a transport error for a
 *      run that was in fact progressing normally.
 *   2. Nothing re-read the migration. `load()` ran once on mount and once after
 *      a mutation resolved — and when the mutation never resolved (see 1), it
 *      never ran again. The only way to see progress was F5.
 *
 * The progress itself was never missing. The runner advances `cursor`,
 * `migrated_objects`, `verified_objects` and `failed_objects` after EVERY item,
 * and writes each item row as it goes. The database knew; the browser never
 * asked. This hook asks.
 *
 * ── WHY POLLING, NOT SOCKET.IO ─────────────────────────────────────────────
 * The project does have a realtime gateway (`backend/src/realtime/`), and it was
 * the first thing considered. It cannot serve this page:
 *
 *   • it authenticates with `JWT_SECRET` and requires `type: 'access'` — a
 *     TENANT token. Platform tokens are signed with `PLATFORM_JWT_SECRET`, so a
 *     platform session is rejected cryptographically, by design;
 *   • its only room is `tenantRoom(tenantId)`, derived from the token. Provider
 *     migrations are a platform-plane operation with no tenant, so there is no
 *     room they belong in;
 *   • it has zero `@SubscribeMessage` handlers on purpose, so there is no
 *     mechanism to subscribe to anything else.
 *
 * Using it would mean inventing platform-plane socket auth, a platform room
 * scheme and a publisher — new security surface, for one page. SSE was rejected
 * by that gateway's own recorded decision (DEC-2) because `EventSource` cannot
 * send an `Authorization` header.
 *
 * Meanwhile the console already solves exactly this problem, in this same
 * domain: `MigrationCenterPage` polls local-path migrations every 2s while a
 * migration is RUNNING and stops otherwise. This hook follows that precedent
 * with tighter bounds. No new infrastructure, no new auth surface, no schema
 * change — and the server stays the single source of truth.
 *
 * ── THE BOUNDS THAT KEEP IT CHEAP ──────────────────────────────────────────
 *   • polls ONLY while the migration is actually moving (RUNNING), or briefly
 *     after a mutation is fired, until a resting state is observed;
 *   • STOPS at every resting and terminal state — a COMPLETED migration is
 *     never polled;
 *   • one request at a time: a poll that is still in flight suppresses the next
 *     tick instead of stacking;
 *   • pauses entirely while the browser tab is hidden, and refreshes once
 *     immediately when it becomes visible again;
 *   • backs off 2s → 4s → 8s → 16s → 30s on consecutive failures and reports
 *     itself as degraded, rather than hammering a struggling backend;
 *   • the item list is refetched only when the run's shape actually changes
 *     (status, or any of the counters), not on every tick.
 *
 * ── WHAT IT WILL NEVER DO ──────────────────────────────────────────────────
 * This hook issues GETs. It imports no mutating API function, so no reconnect,
 * retry, backoff or visibility change can re-run, re-flip or re-finalize
 * anything. A dropped connection resumes READING; it never resumes WRITING.
 */

/** Statuses in which the server is actively changing the migration. */
const LIVE_STATUSES = new Set(['RUNNING']);

/** Statuses that are finished for good. Never polled. */
const TERMINAL_STATUSES = new Set(['COMPLETED', 'FAILED', 'ROLLED_BACK']);

const BASE_INTERVAL_MS = 2_000;
const MAX_INTERVAL_MS = 30_000;

export type LiveConnectionState =
  /** Not polling: the migration is at rest and nothing is changing. */
  | 'idle'
  /** Polling normally. */
  | 'live'
  /** Tab is hidden; polling suspended until it returns. */
  | 'suspended'
  /** Reads are failing; retrying with backoff. The last known state is still shown. */
  | 'degraded';

export interface MigrationLive {
  migration: ProviderMigrationDetailDto | null;
  items: ProviderMigrationItemDto[];
  /** True only for the very first load, so polls never blank the page. */
  loading: boolean;
  /** Fatal error for the INITIAL load only. Poll failures surface as `degraded`. */
  error: string | null;
  connection: LiveConnectionState;
  lastUpdatedAt: number | null;
  /** Consecutive failed polls. 0 whenever the last read succeeded. */
  failureCount: number;
  /** Re-read now. Used after a mutation resolves. Never mutates. */
  refresh: () => Promise<void>;
  /**
   * Start watching because a long-running mutation was just fired.
   *
   * Needed because `POST /run` may not have flipped the status to RUNNING by the
   * time the next tick reads it, and because the run request itself may never
   * resolve in the browser. This opens the watch immediately and it closes on
   * its own once a resting state is observed.
   */
  watchNow: () => void;
}

export function useMigrationLive(id: string): MigrationLive {
  const [migration, setMigration] = useState<ProviderMigrationDetailDto | null>(null);
  const [items, setItems] = useState<ProviderMigrationItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connection, setConnection] = useState<LiveConnectionState>('idle');
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const [failureCount, setFailureCount] = useState(0);

  // ── refs: everything the polling loop needs without re-subscribing ────────
  const mountedRef = useRef(true);
  const inFlightRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef(BASE_INTERVAL_MS);
  const failuresRef = useRef(0);
  /** Set by `watchNow`; cleared once the server reports a resting state. */
  const forcedRef = useRef(false);
  /** Signature of the last payload, so items are refetched only on real change. */
  const shapeRef = useRef<string>('');

  const clearTimer = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  /**
   * One read. Returns the migration so the scheduler can decide what to do next.
   *
   * `initial` distinguishes the first load (which may show a fatal error and a
   * spinner) from a poll (which must never blank an already-rendered page).
   */
  const readOnce = useCallback(
    async (initial: boolean): Promise<ProviderMigrationDetailDto | null> => {
      // Never stack requests: a slow read suppresses the next tick rather than
      // queueing behind it.
      if (inFlightRef.current) return null;
      inFlightRef.current = true;
      try {
        const detail = await getProviderMigration(id);
        if (!mountedRef.current) return null;

        setMigration(detail);
        setLastUpdatedAt(Date.now());
        failuresRef.current = 0;
        setFailureCount(0);
        intervalRef.current = BASE_INTERVAL_MS;
        if (initial) setError(null);

        // Refetch items only when the run's shape actually changed. During a
        // long copy this is every couple of seconds at most, and on a resting
        // migration it is never.
        const shape =
          detail === null
            ? 'null'
            : [
                detail.status,
                detail.migratedObjects,
                detail.verifiedObjects,
                detail.failedObjects,
                detail.skippedObjects,
                detail.ownership?.onTarget ?? -1,
              ].join('|');
        if (shape !== shapeRef.current) {
          shapeRef.current = shape;
          try {
            const rows = await listProviderMigrationItems(id);
            if (mountedRef.current) setItems(rows);
          } catch {
            // The item list is supporting detail; failing to refresh it must not
            // take down the headline progress figures.
          }
        }
        return detail;
      } catch (err) {
        if (!mountedRef.current) return null;
        if (initial) {
          setError(err instanceof Error ? err.message : 'Failed to load the migration.');
          setMigration(null);
          setItems([]);
        } else {
          // A failed POLL is not a page error. Back off and say so.
          failuresRef.current += 1;
          setFailureCount(failuresRef.current);
          intervalRef.current = Math.min(intervalRef.current * 2, MAX_INTERVAL_MS);
          setConnection('degraded');
        }
        return null;
      } finally {
        inFlightRef.current = false;
        if (mountedRef.current && initial) setLoading(false);
      }
    },
    [id],
  );

  /** Should we still be watching, given what the server last told us? */
  const shouldWatch = useCallback((m: ProviderMigrationDetailDto | null): boolean => {
    if (m === null) return forcedRef.current;
    if (TERMINAL_STATUSES.has(m.status)) {
      forcedRef.current = false;
      return false;
    }
    if (LIVE_STATUSES.has(m.status)) return true;
    // A resting, non-terminal state (READY, VERIFYING, PAUSED…) closes a forced
    // watch: the mutation we were watching for has landed.
    forcedRef.current = false;
    return false;
  }, []);

  // ── the scheduler ────────────────────────────────────────────────────────
  // A self-rescheduling timeout, not setInterval: the next tick is only ever
  // scheduled after the previous one has finished, so ticks cannot overlap and
  // the delay can adapt.
  const scheduleRef = useRef<() => void>(() => {});
  scheduleRef.current = () => {
    clearTimer();
    if (!mountedRef.current) return;
    if (!shouldWatch(migration)) {
      setConnection('idle');
      return;
    }
    if (typeof document !== 'undefined' && document.hidden) {
      // Nothing is rendered to anybody right now. Stop reading entirely; the
      // visibility listener resumes with an immediate refresh.
      setConnection('suspended');
      return;
    }
    if (failuresRef.current === 0) setConnection('live');
    timerRef.current = setTimeout(() => {
      void (async () => {
        const next = await readOnce(false);
        // `next` is null when the read was suppressed or failed; keep watching
        // on the current understanding rather than stopping.
        if (next !== null) shouldWatch(next);
        scheduleRef.current();
      })();
    }, intervalRef.current);
  };

  const refresh = useCallback(async () => {
    await readOnce(false);
    scheduleRef.current();
  }, [readOnce]);

  const watchNow = useCallback(() => {
    forcedRef.current = true;
    intervalRef.current = BASE_INTERVAL_MS;
    failuresRef.current = 0;
    setFailureCount(0);
    setConnection('live');
    scheduleRef.current();
  }, []);

  // ── initial load ─────────────────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    setLoading(true);
    void (async () => {
      await readOnce(true);
      scheduleRef.current();
    })();
    return () => {
      // Unmount: stop the loop and make any in-flight response a no-op.
      mountedRef.current = false;
      clearTimer();
    };
  }, [readOnce]);

  // ── react to state changes: start or stop watching as the status moves ────
  useEffect(() => {
    scheduleRef.current();
    return clearTimer;
  }, [migration?.status, migration?.migratedObjects, migration?.failedObjects]);

  // ── tab visibility ───────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVisibility = () => {
      if (document.hidden) {
        clearTimer();
        setConnection((c) => (c === 'idle' ? c : 'suspended'));
        return;
      }
      // Back in view: catch up immediately rather than waiting out an interval.
      void (async () => {
        if (shouldWatch(migration)) await readOnce(false);
        scheduleRef.current();
      })();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [migration, readOnce, shouldWatch]);

  return {
    migration,
    items,
    loading,
    error,
    connection,
    lastUpdatedAt,
    failureCount,
    refresh,
    watchNow,
  };
}
